import { useState } from "react";
import { PageHeader, SectionTitle } from "@/components/base/ui";
import { products } from "@/mocks/products";

const categories = ["All", "Marble", "Granite", "Quartzite", "Travertine"];

export default function Products() {
  const [activeCategory, setActiveCategory] = useState("All");
  const filtered = activeCategory === "All"
    ? products
    : products.filter((p) => p.category === activeCategory);

  return (
    <div>
      <PageHeader
        title="Our Products"
        subtitle="Premium natural stone sourced from world-class quarries. Every slab hand-selected for exceptional quality and beauty."
      />

      {/* Category Filter */}
      <section className="py-8 md:py-12 bg-white border-b border-stone-200">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 text-sm font-sans font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-stone-900 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 md:py-20 bg-stone-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-lg overflow-hidden border border-stone-200 hover:border-stone-300 transition-all duration-300"
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {product.featured && (
                    <span className="absolute top-3 left-3 px-3 py-1 bg-stone-900 text-white text-xs font-sans font-medium rounded-md">
                      Featured
                    </span>
                  )}
                  <span className="absolute top-3 right-3 px-3 py-1 bg-white/90 text-stone-700 text-xs font-sans rounded-md">
                    {product.category}
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-serif text-xl font-semibold text-stone-900 mb-1">
                    {product.name}
                  </h3>
                  <p className="text-gold-500 text-sm font-sans font-medium mb-3">
                    Origin: {product.origin}
                  </p>
                  <p className="text-stone-600 text-sm font-sans leading-relaxed mb-4">
                    {product.description}
                  </p>
                  <div className="pt-4 border-t border-stone-100">
                    <p className="text-stone-500 text-xs font-sans">
                      <span className="text-stone-700 font-medium">Applications:</span>{" "}
                      {product.application}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionTitle
            label="Applications"
            title="Designed for Every Space"
            subtitle="Our natural stone is selected for a wide range of commercial and residential applications."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                icon: "ri-home-smile-line",
                title: "Residential",
                text: "Kitchen countertops, bathroom vanities, flooring, fireplace surrounds, and staircases.",
              },
              {
                icon: "ri-building-4-line",
                title: "Commercial",
                text: "Hotel lobbies, reception desks, office interiors, retail spaces, and restaurant design.",
              },
              {
                icon: "ri-hotel-line",
                title: "Hospitality",
                text: "Luxury hotels, spa interiors, bar tops, feature walls, and entrance halls.",
              },
              {
                icon: "ri-landscape-line",
                title: "Exterior",
                text: "Facade cladding, outdoor pavers, pool surrounds, garden paths, and terraces.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-stone-50 rounded-lg p-6 md:p-8 border border-stone-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-lg border border-stone-200 mb-5">
                  <i className={`${item.icon} text-gold-500 text-xl`}></i>
                </div>
                <h3 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-stone-500 text-sm font-sans leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-stone-900">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Need a Custom Order?
          </h2>
          <p className="text-stone-400 text-base font-sans max-w-xl mx-auto mb-8 leading-relaxed">
            We offer custom slab dimensions, specific thicknesses, and finishes tailored to your project requirements.
          </p>
          <a
            href="/quote"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gold-500 text-white font-sans font-medium text-sm rounded-md hover:bg-gold-600 transition-colors whitespace-nowrap"
          >
            Request a Quotation
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>
    </div>
  );
}